import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { useHistory, withRouter } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import _isEmpty from 'lodash/isEmpty';
import _isArray from 'lodash/isArray';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _includes from 'lodash/includes';
import Fingerprint2 from 'fingerprintjs2';
import { v4 } from 'uuid';

import { nimbusHost, whiteLabelEnabled } from '../../constants/AppConstants';
import {
  getCurrentTime,
  getSystemDetails,
  getTestDuration,
  detectBrowser,
  getServerTime,
  getAndUpdateWebsocketSequence,
  // getBrowserScreenShot,
} from '../../utils/utils';
import RouteChangeTracking from '../Proctor/RouteChangeTracking';
import usePrevious from '../../hooks/usePrevious';
import { validateSession } from '../../api/test';
import { showErrorToast } from '../../actions/toast';
import {
  createdActiveSession,
  emitFailedEventsToSocket,
  emitWebsocketEvent,
  setAlreadyActiveSession,
} from '../../actions/socket';
import { errorLogger, proctoringUBALogger } from '../../utils/trackingUtil';
import {
  ACTIVE_USER_SESSION,
  PROCTOR_TABID_SESSIONSTORAGE_KEY,
} from '../../constants/Proctor';
import {
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE,
  FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY,
  SOCKET_EVENTS,
} from '../../constants/Socket';
import { DEFAULT_ASSESSMENT_TYPE } from '../../constants/Constants';
import {
  evaluateWebsocketBrowserToleranceCount,
  leaveTestViolationAction,
} from '../../actions/proctor';

import { setAudioCount } from '../../actions/audioProblem';

import {
  runProjectResult,
  runVerifyTestCasesResult,
} from '../../actions/desmond';
import { getApiErrorData } from '../../utils/helperFunction';
// import { setFingerPrint } from '../../actions/eventTracking';
// import { env } from '../../utils/request';

let socket;
// const subscribed = null;
function Socket({
  auth,
  test,
  canWebsocketConnectionInitiated,
  initializeSocket,
  socketConnected,
  socketDisconnected,
  testSolutionSet,
  trackSocketConnectionError,
  trackSocketDisconnected,
  trackSocketError,
  fingerPrint,
  trackVisibilityFocus,
  trackVisibilityBlur,
  routeChange,
  showMultiSession,
  enableFullScreen,
  fullScreenEnabled,
  startMonitorSession,
  initSessionMonitorData,
  location,
  trackCaptureScreen,
  showMultiSessionPrompt,
  runCustomInputResult,
  runTestCasesResult,
  assessment,
  toast,
}) {
  const {
    failedSocketEventsStatus,
    shouldSocketDisconnect,
    isSystemTimeMismatch,
    currentServerTime,
    isSessionCreated,
    isSecureBrowserEnable,
    socketActive,
    productTypeSmall,
    socketActiveV2,
  } = useSelector(
    state => ({
      failedSocketEventsStatus: state.socket.failedSocketEventsStatus,
      shouldSocketDisconnect: state.socket.shouldDisconnect,
      isSystemTimeMismatch: state.appStatus.isSystemTimeMismatch,
      currentServerTime: state.appStatus.serverTime,
      isSessionCreated: state.socket.isSessionCreated,
      isSecureBrowserEnable: state.test.isSecureBrowserEnable,
      socketActive: state.socket.socketActive,
      productTypeSmall: state.test.productTypeSmall,
      socketActiveV2: state.socket.socketActiveV2,
    }),
    shallowEqual
  );
  const { username: userName, usertype: userType } = auth.initUserDetails;
  const { is_expired: isTestExpired } = test;
  const isWebProctoringEnabled = _get(test, 'settings.proctor.enabled');
  const previousIsTestExpired = usePrevious(isTestExpired);
  const [isStartTestActionLog, setIsStartTestActionLog] = useState(false);
  const { id, slug, duration: testDuration } = test;
  const [isSessionValidated, setIsSessionValidated] = useState(false);
  const [
    wsBrowserToleranceViolationCount,
    setWsBrowserToleranceViolationCount,
  ] = useState(0);
  const dispatch = useDispatch();
  const history = useHistory();

  const isSessionCreatedRef = useRef(isSessionCreated);

  useEffect(() => {
    isSessionCreatedRef.current = isSessionCreated;
  }, [isSessionCreated]);

  const initSocket = useCallback(
    (assessmentType, assessmentId, sessionId, testSlug, testId, duration) => {
      // checking if it is local
      if (!socket && assessmentType && assessmentId && testSlug) {
        const targetURL =
          window.location.protocol === 'http:'
            ? `ws://${nimbusHost}`
            : `wss://${nimbusHost}`;
        const connectionObject = {
          withCredentials: true,
          query: {
            username: userName,
            contextKey: 'user',
            assessmentType,
            assessmentId,
            testId,
            testSlug,
            testDuration: duration,
            usertype: userType,
          },
        };
        if (sessionId) {
          connectionObject.extraHeaders = { 'x-sessionid': sessionId };
        }
        try {
          socket = io(targetURL, connectionObject);
          socket.on('init', data => {
            if (_isEmpty(data)) {
              return;
            }
            const { connid, wid, sockid } = data;
            if (connid || wid || sockid) {
              initializeSocket(connid, wid, sockid);
            }
          });
          socket.on('connect', () => {
            proctoringUBALogger(
              'info',
              `websocket connected. socket transport type: ${socket?.io?.engine
                ?.transport?.name || 'unknown'}`
            );
            socketConnected(socket);
          });

          socket.on('connect_error', error => {
            errorLogger(error);
            trackSocketConnectionError();
          });
          socket.on('error', error => {
            errorLogger(error);
            trackSocketError();
          });
          socket.on('disconnect', reason => {
            setIsStartTestActionLog(false);
            proctoringUBALogger(
              'info',
              `socket disconnected with reason: ${reason}. socket transport type: ${socket
                ?.io?.engine?.transport?.name || 'unknown'}`
            );
            socketDisconnected();
            trackSocketDisconnected();
          });
          socket.on('proctor', event => {
            if (event.trigger === 'multipleSession') {
              showMultiSessionPrompt();
              dispatch(createdActiveSession({ isCreated: false }));
            }
          });
          // /**
          //  * Run code with custom input results come here
          //  * We previously had a verify button in coding problem
          //  * that runs the code against sample test cases.
          //  * The final result for verify will come in the
          //  * "testcases" property. Though we are not implementing
          //  * verify here, We should not accidentally capture that event
          //  * even if it comes
          //  */
          socket.on('code-async', data => {
            let isRunCodeCustomInputEvent = true;
            if (data && data.testcases) {
              dispatch(runVerifyTestCasesResult(data));
              isRunCodeCustomInputEvent = false;
            }
            if (isRunCodeCustomInputEvent && data) {
              runCustomInputResult(data);
              dispatch(runProjectResult(data));
            }
          });

          // /**
          //  * Run code with test cases results come here
          //  */
          socket.on('code-evaluated', data => {
            runTestCasesResult(data);
          });
          socket.on('session-active', data => {
            dispatch(setAlreadyActiveSession(data));
            history.replace(`/multi-session`);
          });
          socket.on(ACTIVE_USER_SESSION, data => {
            if (
              isSessionCreatedRef.current &&
              data?.currentSession &&
              data?.currentSession !==
                sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY)
            )
              history.replace(`/multi-session`);
          });
          socket.on('session-created', data => {
            dispatch(createdActiveSession(data));
          });
          socket.on(SOCKET_EVENTS.BROWSER_TOLERANCE, data => {
            if (
              data?.tab_id !==
              sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY)
            ) {
              dispatch(leaveTestViolationAction(true));
            }
            const browserToleranceViolationCount = data?.value;
            if (browserToleranceViolationCount) {
              setWsBrowserToleranceViolationCount(
                browserToleranceViolationCount
              );
            }
          });
          socket.on(SOCKET_EVENTS.AUDIO_COMPLETION_COUNT, data => {
            dispatch(setAudioCount(data.audioPlayCountObj || {}));
          });
        } catch (err) {
          errorLogger(err);
        }
      }
    },
    [
      socket,
      nimbusHost,
      userName,
      initializeSocket,
      socketConnected,
      trackSocketConnectionError,
      trackSocketError,
      setIsStartTestActionLog,
      socketDisconnected,
      trackSocketDisconnected,
      showMultiSessionPrompt,
      runCustomInputResult,
      runTestCasesResult,
      fingerPrint,
      dispatch,
      emitFailedEventsToSocket,
      failedSocketEventsStatus,
    ]
  );

  const isExtendTestInitialPhase = useMemo(() => {
    const isExtendTestInInitialPhase =
      testSolutionSet?.add_test_duration &&
      testSolutionSet?.add_test_duration > 0 &&
      !testSolutionSet?.has_resumed_test;

    return isExtendTestInInitialPhase;
  }, [testSolutionSet]);

  useEffect(() => {
    if (socketActive && isWebProctoringEnabled && testSolutionSet.id) {
      const exclusionCriteria =
        isSecureBrowserEnable && getSystemDetails().os === 'macOS'
          ? {}
          : {
              excludes: {
                adBlock: true,
                fonts: true,
                fontsFlash: true,
                webgl: true,
              },
            };
      Fingerprint2.get(exclusionCriteria, components => {
        const neededForHash = [
          'userAgent',
          'language',
          'timezoneOffset',
          'colorDepth',
          'platform',
          'webglVendorAndRenderer',
          'pixelRatio',
          'screenResolution',
          'availableScreenResolution',
        ];
        const browser = detectBrowser();

        const prepareHash = !_isArray(components)
          ? []
          : components.filter(component =>
              _includes(neededForHash, component.key)
            );
        const values = _map(prepareHash, component => component.value);
        const fpHash = Fingerprint2.x64hash128(values.join(''), 31);
        dispatch(
          emitWebsocketEvent({
            eventName: SOCKET_EVENTS.PROCTOR_INIT,
            assessmentId: testSolutionSet.id,
            assessmentType: DEFAULT_ASSESSMENT_TYPE,
            eventData: {
              fingerprint: {
                id: fpHash,
                components: {
                  browser, // for backward compatibility with central
                },
              },
              secure_browser_enabled: isSecureBrowserEnable,
            },
            token: testSolutionSet.token,
            eventGuid: v4(),
            eventCreatedAt: getServerTime().toISOString(),
          })
        );

        if (!failedSocketEventsStatus) {
          // Check and emit browser localstorage has failed socket events
          dispatch(emitFailedEventsToSocket());
        }
      });
    }
  }, [
    testSolutionSet.id,
    socketActive,
    isWebProctoringEnabled,
    isSecureBrowserEnable,
  ]);

  useEffect(() => {
    if (
      socketActiveV2 &&
      isWebProctoringEnabled &&
      testSolutionSet.id &&
      !failedSocketEventsStatus
    ) {
      const failedSocketEventsData = sessionStorage.getItem(
        FAILED_SOCKET_EVENTS_SESSIONSTORAGE_KEY
      );
      if (failedSocketEventsData) {
        const failedEventsValue = failedSocketEventsData
          ? JSON.parse(failedSocketEventsData)
          : FAILED_SOCKET_EVENTS_SESSIONSTORAGE_DEFAULT_VALUE;

        if (Object.entries(failedEventsValue).length) {
          dispatch(emitFailedEventsToSocket());
        }
      }
    }
  }, [
    testSolutionSet.id,
    socketActiveV2,
    isWebProctoringEnabled,
    failedSocketEventsStatus,
  ]);
  useEffect(() => {
    if (
      canWebsocketConnectionInitiated &&
      assessment.assessmentType &&
      assessment.assessmentId &&
      userName &&
      nimbusHost &&
      !socketActive
    ) {
      if (whiteLabelEnabled) {
        validateSession()
          .then(resp => {
            const sessionId = resp.headers['x-sessionid'];
            initSocket(
              assessment.assessmentType,
              assessment.assessmentId,
              sessionId,
              slug,
              id,
              testDuration
            );
          })
          .catch(e => {
            const errorInfo = getApiErrorData(e, 'VDS');
            if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
              showErrorToast(errorInfo.message, errorInfo.apiErrorInfo);
            }
          });
      } else {
        initSocket(
          assessment.assessmentType,
          assessment.assessmentId,
          null,
          slug,
          id,
          testDuration
        );
      }
    }
  }, [
    whiteLabelEnabled,
    canWebsocketConnectionInitiated,
    assessment,
    userName,
    socketActive,
    initSocket,
  ]);

  /**
   * trigger session tracking
   */
  useEffect(() => {
    if (testSolutionSet.id && fingerPrint) {
      const duration = getTestDuration(testSolutionSet);
      const durationInSeconds = Math.floor(duration / 1000);
      const fpHash = _get(fingerPrint, 'trigger.fingerprint.id');
      startMonitorSession({
        fp: fpHash,
        sid: testSolutionSet.id,
        token: testSolutionSet.token,
        maxAge: durationInSeconds, // needed in seconds
      });
    }
  }, [testSolutionSet, test, fingerPrint, startMonitorSession]);

  /**
   * Emit event to start session tracking
   */
  useEffect(() => {
    if (
      initSessionMonitorData &&
      socket &&
      !_isEmpty(fingerPrint) &&
      !isSessionValidated
    ) {
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.VALIDATE_SESSION,
          fp: initSessionMonitorData.fp,
          token: initSessionMonitorData.token,
          sid: initSessionMonitorData.sid,
          maxAge: initSessionMonitorData.maxAge,
          eventGuid: v4(),
          tab_id: sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY),
        })
      );
      setIsSessionValidated(true);
      dispatch(createdActiveSession({ isCreated: true }));
    }
  }, [initSessionMonitorData, fingerPrint, isSessionValidated]);

  /**
   * Disconnect socket on test submit
   */
  useEffect(() => {
    if (
      socket &&
      testSolutionSet.id &&
      (isTestExpired || shouldSocketDisconnect)
    ) {
      socket.disconnect();
    }
  }, [isTestExpired, testSolutionSet.id, shouldSocketDisconnect]);

  useEffect(() => {
    if (socket && testSolutionSet.id && test.slug) {
      const actualBrowserToleranceCount =
        test?.settings?.proctor?.browser_tolerance?.count;
      const remainingBroswerToleranceCount =
        actualBrowserToleranceCount - wsBrowserToleranceViolationCount;
      dispatch(
        evaluateWebsocketBrowserToleranceCount(
          test.slug,
          remainingBroswerToleranceCount
        )
      );
    }
  }, [test.slug, testSolutionSet.id, wsBrowserToleranceViolationCount]);

  /**
   * Emit Window Blur Event
   */
  useEffect(() => {
    if (
      socket &&
      testSolutionSet.id &&
      trackVisibilityBlur &&
      !isExtendTestInitialPhase
    ) {
      proctoringUBALogger('info', 'blur event');
      const eventSequence = getAndUpdateWebsocketSequence(testSolutionSet.id);
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.BLUR_EVENT,
          assessmentId: testSolutionSet.id,
          assessmentType: DEFAULT_ASSESSMENT_TYPE,
          token: testSolutionSet.token,
          eventData: {
            tab_id:
              sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY) || 'N/A',
            eventSequence,
          },
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
        })
      );
    }
  }, [testSolutionSet.id, trackVisibilityBlur, isExtendTestInitialPhase]);

  /**
   * Emit Window Focus Event
   */
  useEffect(() => {
    if (
      socket &&
      testSolutionSet.id &&
      !isExtendTestInitialPhase &&
      trackVisibilityFocus
    ) {
      proctoringUBALogger('info', 'focus event');
      const eventSequence = getAndUpdateWebsocketSequence(testSolutionSet.id);
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.FOCUS_EVENT,
          assessmentId: testSolutionSet.id,
          assessmentType: DEFAULT_ASSESSMENT_TYPE,
          token: testSolutionSet.token,
          eventData: {
            tab_id:
              sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY) || 'N/A',
            eventSequence,
          },
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
        })
      );
    }
  }, [testSolutionSet.id, trackVisibilityFocus, isExtendTestInitialPhase]);

  /**
   * Emit route change events
   */
  useEffect(() => {
    if (socket && routeChange && testSolutionSet.id) {
      proctoringUBALogger('info', 'location url changed');
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.LOCATION_URL_CHANGED,
          assessmentId: testSolutionSet.id,
          assessmentType: DEFAULT_ASSESSMENT_TYPE,
          eventData: routeChange,
          token: testSolutionSet.token,
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
          tab_id: sessionStorage.getItem(PROCTOR_TABID_SESSIONSTORAGE_KEY),
        })
      );
    }
  }, [routeChange, testSolutionSet.id]);

  /**
   * Emit time mismatch events
   */
  useEffect(() => {
    if (socket && testSolutionSet.id && isSystemTimeMismatch) {
      proctoringUBALogger('info', 'time mismatch');
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.TIME_MISMATCH,
          assessmentId: testSolutionSet.id,
          assessmentType: DEFAULT_ASSESSMENT_TYPE,
          token: testSolutionSet.token,
          eventGuid: v4(),
          eventCreatedAt: getServerTime().toISOString(),
        })
      );
    }
  }, [socket, testSolutionSet.id, isSystemTimeMismatch, currentServerTime]);

  /**
   * If Multisession prompt is openned and Full-screen prompt is also openned
   * close fullscreen prompt
   */
  useEffect(() => {
    if (showMultiSession && !fullScreenEnabled) {
      proctoringUBALogger(
        'Proctor:FullScreen',
        'enableFullScreen() called from Socket due to multi-session prompt'
      );
      enableFullScreen();
    }
  }, [showMultiSession, fullScreenEnabled, enableFullScreen]);

  /**
   * Emit Track and Capture screen events
   */

  const sendTrackEvent = useCallback(
    (pathname, action) => {
      const systemInfo = getSystemDetails();
      systemInfo.tzoffset = new Date().getTimezoneOffset();
      systemInfo.client_time = getCurrentTime().toISOString();
      const companySlug = _get(test, 'company.slug');
      let problemSlug;
      const params = pathname ? pathname.split('/') : [];
      /**
       * Problem slug is only present if we are in solution page
       */
      if (params[3] === 'problem') {
        /* eslint-disable-next-line prefer-destructuring */
        problemSlug = params[4];
      }
      const payload = {
        system: systemInfo,
        username: userName,
        testSlug: test.slug,
        problemSlug: problemSlug || undefined,
        isTestEnv: true,
        isNewTestInterface: true,
        sockid: socket.id,
        company: companySlug,
        userType,
        href: window.location.href,
        token: testSolutionSet.token,
        action,
        eventGuid: v4(),
      };
      dispatch(
        emitWebsocketEvent({
          eventName: SOCKET_EVENTS.TRACK,
          ...payload,
        })
      );
    },
    [
      socket,
      userName,
      test,
      window.location.href,
      testSolutionSet.token,
      getSystemDetails,
      getCurrentTime,
    ]
  );

  useEffect(() => {
    let action;
    if (!isStartTestActionLog && testSolutionSet.id && !isTestExpired) {
      setIsStartTestActionLog(true);
      action = 'START_TEST';
    } else if (
      testSolutionSet.id &&
      isTestExpired &&
      isTestExpired !== previousIsTestExpired
    ) {
      action = 'END_TEST';
    }
    if (socket && userName) {
      sendTrackEvent(location.pathname, action);
    }
  }, [
    isStartTestActionLog,
    location.pathname,
    testSolutionSet.id,
    isTestExpired,
  ]);

  const trackChangeRoute = () => {
    trackCaptureScreen();
  };

  return (
    <>
      <Modal
        isOpen={showMultiSession}
        size="lg"
        backdrop="static"
        keyboard={false}
        centered
      >
        <ModalBody>
          <p className="text-danger text-center">
            This {productTypeSmall} session is currently open in another
            tab/window. <br />
            Please close either of these and hit refresh.
          </p>
        </ModalBody>
      </Modal>
      <RouteChangeTracking
        location={location}
        trackChangeRoute={trackChangeRoute}
      />
    </>
  );
}

export default withRouter(Socket);
